/**
 * Gedeelde toets-beslissingen voor globale (document/window) keydown-
 * listeners. Pure functies zodat ze zonder DOM te testen zijn.
 *
 * Achtergrond: drie globale listeners grepen toetsen die niet voor hen
 * bedoeld waren — Delete in de (gesloten) Explorer bood het actieve
 * diagram ter verwijdering aan, Cmd/Ctrl+Shift+V (plakken als platte
 * tekst) werd in productie ingeslikt, en Option/Alt+W sloot een tab
 * terwijl je in een veld typte.
 */
import type { FocusScope } from "../state/keymap";

/**
 * Staat de toets in een tekstveld? Input, textarea, select, contenteditable
 * of CodeMirror (`.cm-editor`). Duck-typed zodat het ook op SVG-elementen
 * en in een DOM-loze testomgeving werkt.
 */
export function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || typeof (el as Element).tagName !== "string") return false;
  const node = el as HTMLElement;
  const tag = node.tagName.toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (node.isContentEditable) return true;
  if (typeof node.closest === "function" && node.closest(".cm-editor")) return true;
  return false;
}

type KeyLike = Pick<KeyboardEvent, "key" | "code" | "altKey" | "metaKey" | "ctrlKey" | "shiftKey">;

/**
 * Alt/Option+W — sluit het actieve diagram-tab. Op `code` gematcht omdat
 * Option op de Mac "∑" als `key` levert.
 */
export function isCloseActiveTabShortcut(e: KeyLike): boolean {
  return e.altKey && !e.metaKey && !e.ctrlKey && (e.code === "KeyW" || e.key.toLowerCase() === "w");
}

/** Cmd/Ctrl+Shift+V — togglet het dev-paneel met legacy viewState-callsites. */
export function isDevWarningsToggleShortcut(e: KeyLike): boolean {
  return (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "v";
}

/**
 * Mag de Explorer Delete/Backspace opvatten als "selectie verwijderen"?
 * Alleen als het paneel open is én de gebruiker er werkt (scope of DOM-
 * focus). De selectie volgt het actieve diagram, dus zonder deze grens
 * bood een Delete op het canvas dat diagram ter verwijdering aan.
 */
export function explorerOwnsDeleteKey(opts: {
  open: boolean;
  scope: FocusScope;
  focusInside: boolean;
}): boolean {
  if (!opts.open) return false;
  return opts.scope === "explorer" || opts.focusInside;
}
